import type { ImportInfo, ImportStatus } from '@mcgames/contracts';
import { useQueryClient } from '@tanstack/react-query';
import { useApiMutation, useApiQuery } from '../api/hooks.ts';
import { gamePath } from './games/game-hooks.ts';
import { importActionPath, latestImportPath } from './games/import-hooks.ts';
import { importPath } from './indexer/indexer-hooks.ts';

export const IMPORTS_PATH = '/api/admin/imports';
const LIST_POLL_MS = 5000;

/** Status filter of the list; empty lists every import. */
export type ImportsFilter = ImportStatus | '';

export function importsListPath(status: ImportsFilter): string {
  return status === '' ? IMPORTS_PATH : `${IMPORTS_PATH}?status=${encodeURIComponent(status)}`;
}

/** Refetch interval of the list: every 5 s for as long as a listed import runs. */
export function importsPollInterval(imports: ImportInfo[] | undefined): number | false {
  return imports?.some((info) => info.status === 'running') === true ? LIST_POLL_MS : false;
}

/** The newest imports (at most 200), optionally of one status. */
export function useImports(status: ImportsFilter) {
  return useApiQuery<ImportInfo[]>(importsListPath(status), {
    refetchInterval: (query) => importsPollInterval(query.state.data),
  });
}

/** What can be done with an import: retry or discard a stopped one, cancel a running one. */
export type ImportAction = 'retry' | 'discard' | 'cancel';

/**
 * Runs an action on an import. The promise returned from `onSuccess` keeps the mutation
 * pending until every list, the import itself and the game it belongs to afterwards show the new
 * state. A game the action deleted (a discarded draft) is not refetched.
 */
export function useImportAction(info: ImportInfo, action: ImportAction) {
  const queryClient = useQueryClient();
  return useApiMutation<void, ImportInfo>('POST', importActionPath(info.id, action), {
    onSuccess: async (result) => {
      const keys = [
        queryClient.invalidateQueries({
          predicate: ({ queryKey }) =>
            typeof queryKey[0] === 'string' &&
            (queryKey[0] === IMPORTS_PATH || queryKey[0].startsWith(`${IMPORTS_PATH}?`)),
        }),
        queryClient.invalidateQueries({ queryKey: [importPath(info.id)] }),
      ];
      const gameId = action === 'discard' ? result.gameId : info.gameId;
      if (gameId !== null) {
        keys.push(
          queryClient.invalidateQueries({ queryKey: [gamePath(gameId)] }),
          queryClient.invalidateQueries({ queryKey: [latestImportPath(gameId)] }),
        );
      }
      await Promise.all(keys);
    },
  });
}

/** Retries a stopped import (see `useImportAction`). */
export function useRetryListedImport(info: ImportInfo) {
  return useImportAction(info, 'retry');
}
